import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Save, X, AlarmClock } from 'lucide-react';
import api from '../../../api/config';

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

const toMin = (t) => {
  const [h, m] = (t || '00:00').split(':').map(Number);
  return h * 60 + m;
};

export default function ScheduleManager() {
  const [schedules, setSchedules] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  const EMPTY = {
    nombre: '', hora_inicio: '09:00', hora_fin: '18:00',
    ventana_entrada_inicio: '08:30', ventana_entrada_fin: '11:00',
    ventana_salida_inicio: '15:00', ventana_salida_fin: '18:30',
    minutos_tolerancia_tarde: 10, minutos_tolerancia_salida_temprano: 10,
    hora_inicio_overtime: '18:00',
    calcular_break_en_asistencia: true, contar_entrada_temprana_overtime: false,
    activo: true,
  };

  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/schedules');
      setSchedules(r.data.schedules || []);
    } finally { setLoading(false); }
  };

  const handleSelect = (s) => {
    setSelected(s);
    setForm({
      nombre: s.nombre, hora_inicio: s.hora_inicio, hora_fin: s.hora_fin,
      ventana_entrada_inicio: s.ventana_entrada_inicio || '08:30',
      ventana_entrada_fin:    s.ventana_entrada_fin    || '11:00',
      ventana_salida_inicio:  s.ventana_salida_inicio  || '15:00',
      ventana_salida_fin:     s.ventana_salida_fin     || '18:30',
      minutos_tolerancia_tarde:           s.minutos_tolerancia_tarde           || 10,
      minutos_tolerancia_salida_temprano: s.minutos_tolerancia_salida_temprano || 10,
      hora_inicio_overtime:               s.hora_inicio_overtime               || '18:00',
      calcular_break_en_asistencia:     s.calcular_break_en_asistencia     ?? true,
      contar_entrada_temprana_overtime: s.contar_entrada_temprana_overtime ?? false,
      activo: s.activo ?? true,
    });
  };

  const handleNew = () => { setSelected(null); setForm(EMPTY); };

  const handleSave = async () => {
    if (!form.nombre.trim()) return alert('Ingresa un nombre para el horario.');
    try {
      setSaving(true);
      if (selected) {
        await api.put(`/schedules/${selected.id}`, form);
      } else {
        await api.post('/schedules', form);
      }
      await load();
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este horario?')) return;
    try {
      await api.delete(`/schedules/${id}`);
      await load();
      if (selected?.id === id) handleNew();
    } catch (e) {
      alert('Error al eliminar: ' + (e.response?.data?.error || e.message));
    }
  };

  const workMinutes = toMin(form.hora_fin) - toMin(form.hora_inicio);
  const leftPct  = toMin(form.hora_inicio) / 14.4;
  const widthPct = Math.max(0, workMinutes) / 14.4;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">

      {/* ── Lista ─────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-800">Horarios</span>
          <button
            onClick={handleNew}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus size={13} /> Nuevo
          </button>
        </div>

        <div className="divide-y divide-gray-50 max-h-[580px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Clock size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No hay horarios</p>
            </div>
          ) : (
            schedules.map(s => (
              <button
                key={s.id}
                onClick={() => handleSelect(s)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                  selected?.id === s.id
                    ? 'bg-blue-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  selected?.id === s.id ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Clock size={14} className={selected?.id === s.id ? 'text-blue-600' : 'text-gray-500'} />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${selected?.id === s.id ? 'text-blue-700' : 'text-gray-800'}`}>
                    {s.nombre}
                  </p>
                  <p className="text-xs text-gray-400">{s.hora_inicio} – {s.hora_fin}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Formulario ─────────────────────────────────── */}
      <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 shadow-sm">
        {/* Form header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {selected ? 'Editar horario' : 'Nuevo horario'}
            </h3>
            {selected && <p className="text-xs text-gray-400 mt-0.5">{selected.nombre}</p>}
          </div>
          <div className="flex gap-2">
            {selected && (
              <button
                onClick={() => handleDelete(selected.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 size={13} /> Eliminar
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : <Save size={13} />}
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Nombre */}
          <div className="max-w-sm">
            <label className={labelCls}>Nombre del horario</label>
            <input
              type="text" value={form.nombre}
              onChange={e => set('nombre', e.target.value)}
              placeholder="Ej: Administrativo 9-6"
              className={inputCls}
            />
          </div>

          {/* Horas principales */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Horario de trabajo</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className={labelCls}>Hora inicio</label>
                <input type="time" value={form.hora_inicio} onChange={e => set('hora_inicio', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Hora fin</label>
                <input type="time" value={form.hora_fin} onChange={e => set('hora_fin', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tolerancia entrada (min)</label>
                <input type="number" value={form.minutos_tolerancia_tarde}
                  onChange={e => set('minutos_tolerancia_tarde', parseInt(e.target.value))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tolerancia salida (min)</label>
                <input type="number" value={form.minutos_tolerancia_salida_temprano}
                  onChange={e => set('minutos_tolerancia_salida_temprano', parseInt(e.target.value))} className={inputCls} />
              </div>
            </div>
          </div>

          {/* Ventanas */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Ventanas válidas</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Ventana de entrada</label>
                <div className="flex items-center gap-2">
                  <input type="time" value={form.ventana_entrada_inicio}
                    onChange={e => set('ventana_entrada_inicio', e.target.value)} className={inputCls} />
                  <span className="text-xs text-gray-400 flex-shrink-0">a</span>
                  <input type="time" value={form.ventana_entrada_fin}
                    onChange={e => set('ventana_entrada_fin', e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Ventana de salida</label>
                <div className="flex items-center gap-2">
                  <input type="time" value={form.ventana_salida_inicio}
                    onChange={e => set('ventana_salida_inicio', e.target.value)} className={inputCls} />
                  <span className="text-xs text-gray-400 flex-shrink-0">a</span>
                  <input type="time" value={form.ventana_salida_fin}
                    onChange={e => set('ventana_salida_fin', e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Inicio overtime</label>
                <input type="time" value={form.hora_inicio_overtime}
                  onChange={e => set('hora_inicio_overtime', e.target.value)} className={inputCls} />
              </div>
            </div>
          </div>

          {/* Opciones */}
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.calcular_break_en_asistencia}
                onChange={e => set('calcular_break_en_asistencia', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300" />
              <span className="text-sm text-gray-700">Calcular descanso en asistencia</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.contar_entrada_temprana_overtime}
                onChange={e => set('contar_entrada_temprana_overtime', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300" />
              <span className="text-sm text-gray-700">Entrada temprana como overtime</span>
            </label>
          </div>

          {/* Gráfica */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Vista previa</p>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              {/* Time axis */}
              <div className="flex mb-3">
                <div className="w-0"></div>
                <div className="flex-1 relative h-4">
                  {/* Grid lines */}
                  {Array.from({ length: 13 }, (_, i) => (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0 border-l border-gray-200"
                      style={{ left: `${(i * 2 / 24) * 100}%` }}
                    />
                  ))}
                  {Array.from({ length: 13 }, (_, i) => (
                    <span
                      key={i}
                      className="absolute text-xs text-gray-400 -translate-x-1/2 select-none"
                      style={{ left: `${(i * 2 / 24) * 100}%` }}
                    >
                      {String(i * 2).padStart(2, '0')}:00
                    </span>
                  ))}
                </div>
              </div>

              {/* Bar */}
              <div className="relative h-10 bg-white border border-gray-200 rounded-lg overflow-hidden">
                {/* Grid lines */}
                {Array.from({ length: 13 }, (_, i) => (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 border-l border-gray-100"
                    style={{ left: `${(i * 2 / 24) * 100}%` }}
                  />
                ))}
                <div
                  className="absolute h-full bg-blue-500 rounded flex items-center justify-center"
                  style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                >
                  {widthPct > 8 && (
                    <span className="text-white text-xs font-semibold px-1 truncate">
                      {form.hora_inicio} – {form.hora_fin}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <AlarmClock size={12} />
                Duración: {Math.floor(Math.max(0, workMinutes) / 60)}h {Math.max(0, workMinutes) % 60}min
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
